import { useEffect, useRef } from "react";
import { Outlet, Navigate, useLocation } from "react-router-dom";
import { useMe } from "../hooks/useApi";
import { useTheme } from "../hooks/useTheme";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import MobileHeader from "../components/MobileHeader";
import MobileFooterNav from "../components/MobileFooterNav";
import AnnouncementBanner from "../components/AnnouncementBanner";

// Map dashboard routes to the placement key admins use to target system messages.
const ROUTE_TO_PLACEMENT: Array<[string, string]> = [
  ["/partners", "partners"],
  ["/api-keys", "api-keys"],
  ["/subscription", "subscription"],
  ["/support", "support"],
  ["/dashboard", "dashboard"],
  ["/profile", "profile"],
  ["/wallet", "wallet"],
  ["/usage", "usage"],
  ["/models", "models"],
  ["/logs", "logs"],
  ["/docs", "docs"],
];

const placementFromPath = (pathname: string): string | undefined => {
  const match = ROUTE_TO_PLACEMENT.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
  return match?.[1];
};

export default function DashboardLayout() {
  const { data: user, isLoading, error } = useMe();
  const { theme } = useTheme();
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Reset scroll to top when navigating between tabs/pages (fixes MOBILE & DESKTOP).
  // The scroll container is <main> (overflow-y-auto), not window.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
    // Fallback for browsers that don't support "instant"
    if (mainRef.current && mainRef.current.scrollTop !== 0) {
      mainRef.current.scrollTop = 0;
    }
    window.scrollTo({ top: 0, left: 0, behavior: "instant" as ScrollBehavior });
  }, [location.pathname]);

  // Show loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-base">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin h-8 w-8 text-primary" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-body text-text-secondary">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  // Not authenticated, redirect to login
  if (!user || error) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div data-theme={theme} className="flex h-screen bg-surface-base">
      <div className="hidden md:flex shrink-0">
        <Sidebar />
      </div>
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="hidden md:block">
          <Header />
        </div>
        <MobileHeader />
        <main ref={mainRef} className="flex-1 overflow-y-auto px-4 md:px-6 pt-[76px] md:pt-6 pb-28 md:pb-6 max-w-page mx-auto w-full">
          <div className="space-y-6">
            <AnnouncementBanner placement={placementFromPath(location.pathname)} />
            <Outlet />
          </div>
        </main>
      </div>
      <MobileFooterNav />
    </div>
  );
}